import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Search, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';

interface FAQ {
  id: string;
  category: string;
  question: string;
  answer: string;
  order_index: number;
  helpful_count: number;
  not_helpful_count: number;
  is_published: boolean;
}

const FAQ = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('todas');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const categories = [
    { value: 'todas', label: 'Todas las Categorías' },
    { value: 'general', label: 'Preguntas Generales' },
    { value: 'cursos', label: 'Sobre los Cursos' },
    { value: 'campanas', label: 'Sobre las Campañas' },
    { value: 'cuenta', label: 'Mi Cuenta' },
    { value: 'pagos', label: 'Pagos y Suscripciones' },
    { value: 'tecnico', label: 'Soporte Técnico' }
  ];

  useEffect(() => {
    loadFAQs();
  }, [selectedCategory]);

  const loadFAQs = async () => {
    try {
      let query = supabase
        .from('faqs')
        .select('*')
        .eq('is_published', true)
        .order('order_index', { ascending: true });

      if (selectedCategory !== 'todas') {
        query = query.eq('category', selectedCategory);
      }

      const { data, error } = await query;

      if (error) throw error;
      setFaqs(data || []);
    } catch (error) {
      console.error('Error al cargar FAQs:', error);
      toast.error('Error al cargar las preguntas frecuentes');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (faqId: string, isHelpful: boolean) => {
    try {
      const faq = faqs.find(f => f.id === faqId);
      if (!faq) return;

      const { error } = await supabase
        .from('faqs')
        .update({
          helpful_count: isHelpful ? faq.helpful_count + 1 : faq.helpful_count,
          not_helpful_count: !isHelpful ? faq.not_helpful_count + 1 : faq.not_helpful_count
        })
        .eq('id', faqId);

      if (error) throw error;

      toast.success(isHelpful ? '¡Gracias por tu feedback positivo!' : 'Gracias por tu feedback');
      loadFAQs();
    } catch (error) {
      console.error('Error al votar:', error);
    }
  };

  const filteredFAQs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <HelpCircle className="w-16 h-16 mx-auto mb-6" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Preguntas Frecuentes
            </h1>
            <p className="text-xl md:text-2xl opacity-90">
              Encuentra respuestas a las preguntas más comunes sobre nuestra plataforma
            </p>
          </div>
        </div>
      </section>

      {/* Búsqueda y Filtros */}
      <section className="py-8 bg-white shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Búsqueda */}
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar en las preguntas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Categorías */}
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Lista de FAQs */}
      <section className="py-12 container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Cargando preguntas...</p>
            </div>
          ) : filteredFAQs.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <HelpCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No se encontraron preguntas
              </h3>
              <p className="text-gray-600 mb-6">
                {searchTerm
                  ? 'Intenta con otros términos de búsqueda'
                  : 'Aún no hay preguntas frecuentes en esta categoría'}
              </p>
              <a
                href="/contact"
                className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Contactar Soporte
              </a>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredFAQs.map((faq) => (
                <div
                  key={faq.id}
                  className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  {/* Pregunta */}
                  <button
                    onClick={() => toggleExpand(faq.id)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-lg"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    {expandedId === faq.id ? (
                      <ChevronUp className="w-6 h-6 text-blue-600 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-6 h-6 text-gray-400 flex-shrink-0" />
                    )}
                  </button>

                  {/* Respuesta */}
                  {expandedId === faq.id && (
                    <div className="px-6 pb-6 border-t border-gray-100">
                      <div className="pt-4">
                        <p className="text-gray-700 leading-relaxed mb-4">
                          {faq.answer}
                        </p>

                        {/* Botones de Feedback */}
                        <div className="flex items-center gap-4 pt-4 border-t border-gray-100">
                          <span className="text-sm text-gray-600">
                            ¿Te fue útil esta respuesta?
                          </span>
                          <button
                            onClick={() => handleVote(faq.id, true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition-colors text-sm"
                          >
                            <ThumbsUp className="w-4 h-4" />
                            Sí ({faq.helpful_count})
                          </button>
                          <button
                            onClick={() => handleVote(faq.id, false)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg transition-colors text-sm"
                          >
                            <ThumbsDown className="w-4 h-4" />
                            No ({faq.not_helpful_count})
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA de Contacto */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center bg-white rounded-2xl shadow-lg p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              ¿No encontraste lo que buscabas?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Nuestro equipo de soporte está listo para ayudarte con cualquier pregunta adicional.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg"
              >
                Contactar Soporte
              </a>
              <a
                href="/ayuda"
                className="px-8 py-4 bg-gray-100 text-gray-900 rounded-lg font-semibold hover:bg-gray-200 transition-all"
              >
                Centro de Ayuda
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
